import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  FiDownload, FiX, FiChevronLeft, FiChevronRight, 
  FiBookOpen, FiFileText, FiMaximize, FiMinimize, 
  FiZoomIn, FiZoomOut, FiRotateCcw, FiLayers, FiExternalLink
} from 'react-icons/fi';
import './BookCVModal.css';
import * as pdfjsLib from 'pdfjs-dist';

// Configure local PDF worker
if (typeof window !== 'undefined' && pdfjsLib.GlobalWorkerOptions) {
  try {
    pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
  } catch (e) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
  }
}

// Resilient Error Boundary to safeguard the modal
class CVErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    console.warn("CV Boundary caught an error, rendering fallback reader:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="book-reader-container">
          <iframe
            src={`${this.props.pdfUrl}#toolbar=1`}
            title="Curriculum Vitae"
            className="book-reader-iframe"
          />
        </div>
      );
    }
    return this.props.children;
  }
}

// High-DPI Canvas Page Renderer using vanilla pdfjs-dist
const PDFPageCanvas = ({ pdfDoc, pageNumber, width }) => {
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const [isRendered, setIsRendered] = useState(false);
  const [renderError, setRenderError] = useState(null);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current || !pageNumber) return;

    let isMounted = true;
    setIsRendered(false);
    setRenderError(null);

    const renderPage = async () => {
      try {
        if (renderTaskRef.current) {
          try {
            renderTaskRef.current.cancel();
          } catch (_) {}
          renderTaskRef.current = null;
        }

        const page = await pdfDoc.getPage(pageNumber);
        if (!isMounted) return;

        const baseViewport = page.getViewport({ scale: 1.0 });
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        const scale = (width / baseViewport.width) * pixelRatio;
        const viewport = page.getViewport({ scale });

        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d', { alpha: false });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${Math.floor(width)}px`;
        canvas.style.height = `${Math.floor(width * (baseViewport.height / baseViewport.width))}px`;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;

        await renderTask.promise;
        if (isMounted) {
          setIsRendered(true);
        }
      } catch (err) {
        if (err?.name !== 'RenderingCancelledException' && isMounted) {
          console.warn(`Canvas render note on page ${pageNumber}:`, err);
          setRenderError(err.message);
        }
      }
    };

    renderPage();

    return () => {
      isMounted = false;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (_) {}
      }
    };
  }, [pdfDoc, pageNumber, width]);

  return (
    <div className="book-pdf-canvas-container" style={{ position: 'relative', width: `${width}px` }}>
      {!isRendered && !renderError && (
        <div 
          className="canvas-page-placeholder" 
          style={{
            width: `${width}px`,
            height: `${Math.floor(width * 1.414)}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#faf9f6'
          }}
        >
          <div className="spinner-ring" style={{ width: 32, height: 32 }}></div>
        </div>
      )}
      <canvas
        ref={canvasRef}
        className="book-pdf-canvas"
        style={{ display: isRendered ? 'block' : 'none', width: '100%', height: 'auto' }}
      />
    </div>
  );
};

export default function BookCVModal({ isOpen, onClose, pdfUrl = "/Marisilin_Bijosilin_Resume.pdf" }) {
  const [isBookOpened, setIsBookOpened] = useState(false);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('spread'); // 'spread' | 'single' | 'reader'
  const [scale, setScale] = useState(1);
  const [pageWidth, setPageWidth] = useState(440);
  const [isLoadingPdf, setIsLoadingPdf] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const modalRef = useRef(null);

  // Trigger book opening animation on modal open
  useEffect(() => {
    if (isOpen) {
      setIsBookOpened(false);
      const timer = setTimeout(() => {
        setIsBookOpened(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setIsBookOpened(false);
      setCurrentPage(1);
    }
  }, [isOpen]);

  // Load PDF Document via vanilla pdfjs-dist
  useEffect(() => {
    if (!isOpen) return;

    let isCancelled = false;
    setIsLoadingPdf(true);
    setLoadError(null);

    const loadDoc = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          cMapUrl: 'https://unpkg.com/pdfjs-dist/cmaps/',
          cMapPacked: true,
        });

        const doc = await loadingTask.promise;
        if (!isCancelled) {
          setPdfDoc(doc);
          setNumPages(doc.numPages);
          setIsLoadingPdf(false);
        }
      } catch (err) {
        console.warn("pdfjs-dist load failed, switching to native reader fallback:", err);
        if (!isCancelled) {
          setLoadError(err.message || 'Unable to load interactive book mode');
          setViewMode('reader');
          setIsLoadingPdf(false);
        }
      }
    };

    loadDoc();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, pdfUrl]);

  // Responsive page width calculation
  const updateDimensions = useCallback(() => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const isMobile = w < 860;

    if (isMobile) {
      setViewMode(prev => prev === 'reader' ? 'reader' : 'single');
      const maxW = Math.min(w * 0.9, 580);
      const maxH = (h - 180) / 1.414;
      setPageWidth(Math.min(maxW, maxH) * scale);
    } else {
      const availableW = Math.min(w * 0.92, 1180);
      const availableH = h - 160;
      
      if (viewMode === 'spread') {
        const fitWBySpread = (availableW - 80) / 2;
        const fitWByHeight = (availableH - 40) / 1.414;
        setPageWidth(Math.min(fitWBySpread, fitWByHeight, 520) * scale);
      } else {
        const fitWByHeight = (availableH - 40) / 1.414;
        setPageWidth(Math.min(availableW * 0.7, fitWByHeight, 680) * scale);
      }
    }
  }, [viewMode, scale]);

  useEffect(() => {
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [updateDimensions]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') handlePrevPage();
      else if (e.key === 'ArrowRight') handleNextPage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentPage, numPages, viewMode, onClose]);

  const handleNextPage = () => {
    if (!numPages) return;
    const step = viewMode === 'spread' ? 2 : 1;
    if (currentPage + step <= numPages) {
      setCurrentPage(prev => prev + step);
    }
  };

  const handlePrevPage = () => {
    const step = viewMode === 'spread' ? 2 : 1;
    if (currentPage - step >= 1) {
      setCurrentPage(prev => Math.max(1, prev - step));
    }
  };

  const replayOpenAnimation = () => {
    setIsBookOpened(false);
    setTimeout(() => setIsBookOpened(true), 350);
  };

  if (!isOpen) return null;

  return (
    <div className="book-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Curriculum Vitae of Marisilin Bijosilin">
      <div 
        ref={modalRef}
        className={`book-modal-stage ${isBookOpened ? 'book-state-opened' : 'book-state-closed'}`} 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Control Navigation Bar */}
        <header className="book-top-bar">
          <div className="book-meta-info">
            <div className="book-icon-badge">
              <FiBookOpen />
            </div>
            <div>
              <h3 className="book-author-title">Marisilin Bijosilin</h3>
              <span className="book-sub-badge">Curriculum Vitae • Interactive Book View</span>
            </div>
          </div>

          <div className="book-controls-group">
            {/* View Mode Toggle */}
            <div className="book-view-toggle">
              <button
                className={`ctrl-btn ${viewMode === 'spread' ? 'active' : ''}`}
                onClick={() => setViewMode('spread')}
                title="Two-Page Spread View"
                aria-label="Two-Page Spread View"
              >
                <FiBookOpen /> <span className="btn-label-text">Spread</span>
              </button>
              <button
                className={`ctrl-btn ${viewMode === 'single' ? 'active' : ''}`}
                onClick={() => setViewMode('single')}
                title="Single Page View"
                aria-label="Single Page View"
              >
                <FiFileText /> <span className="btn-label-text">Single</span>
              </button>
              <button
                className={`ctrl-btn ${viewMode === 'reader' ? 'active' : ''}`}
                onClick={() => setViewMode('reader')}
                title="Direct PDF Reader View"
                aria-label="Direct PDF Reader View"
              >
                <FiMaximize /> <span className="btn-label-text">Reader</span>
              </button>
            </div>

            {/* Zoom Controls (Active in Book Mode) */}
            {viewMode !== 'reader' && (
              <div className="book-zoom-btns">
                <button 
                  className="ctrl-btn-icon" 
                  onClick={() => setScale(s => Math.max(0.8, s - 0.1))} 
                  title="Zoom Out"
                  aria-label="Zoom Out"
                  disabled={scale <= 0.8}
                >
                  <FiZoomOut />
                </button>
                <span className="zoom-indicator">{Math.round(scale * 100)}%</span>
                <button 
                  className="ctrl-btn-icon" 
                  onClick={() => setScale(s => Math.min(1.4, s + 0.1))} 
                  title="Zoom In"
                  aria-label="Zoom In"
                  disabled={scale >= 1.4}
                >
                  <FiZoomIn />
                </button>
              </div>
            )}

            {/* Replay Book Animation */}
            {viewMode !== 'reader' && (
              <button 
                className="ctrl-btn-icon" 
                onClick={replayOpenAnimation}
                title="Re-open Book Animation"
                aria-label="Re-open Book Animation"
              >
                <FiRotateCcw />
              </button>
            )}

            {/* Open in New Tab */}
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ctrl-btn-icon"
              title="Open PDF in New Tab"
              aria-label="Open PDF in New Tab"
            >
              <FiExternalLink />
            </a>

            {/* Download PDF */}
            <a
              href={pdfUrl}
              download="Marisilin_Bijosilin_Resume.pdf"
              className="book-download-btn"
              title="Download Official PDF"
              aria-label="Download Official PDF"
            >
              <FiDownload /> <span>Download</span>
            </a>

            {/* Close Button */}
            <button 
              className="book-close-btn" 
              onClick={onClose} 
              title="Close Book (Esc)"
              aria-label="Close Book"
            >
              <FiX />
            </button>
          </div>
        </header>

        {/* Main 3D Book Reading Theater */}
        <main className="book-viewport">
          <CVErrorBoundary pdfUrl={pdfUrl}>
            {viewMode === 'reader' ? (
              <div className="book-reader-container">
                <iframe
                  src={`${pdfUrl}#toolbar=1&navpanes=0`}
                  title="Marisilin Bijosilin Curriculum Vitae Reader"
                  className="book-reader-iframe"
                />
              </div>
            ) : (
              <div className={`book-3d-container ${isBookOpened ? 'is-opened' : 'is-closed'}`}>
                {/* 3D Book Hardcover Front (flips open during animation) */}
                <div className="book-hardcover-front">
                  <div className="hardcover-leather-texture">
                    <div className="hardcover-gold-border">
                      <div className="cover-gold-crest">
                        <FiLayers />
                      </div>
                      <h1 className="cover-title-name">MARISILIN BIJOSILIN</h1>
                      <div className="cover-gold-divider"></div>
                      <h2 className="cover-subtitle">CURRICULUM VITAE</h2>
                      <p className="cover-tagline">Junior Software Engineer • Portfolio 2026</p>
                      
                      <div className="cover-bottom-stamp">
                        <span>SIMATS MOBILITY • WECONN3CT GMBH • SLIIT SE</span>
                      </div>
                    </div>
                  </div>
                  <div className="cover-spine-edge"></div>
                </div>

                {/* 3D Book Hardcover Back (foundation base) */}
                <div className="book-hardcover-back">
                  <div className="hardcover-back-shadow"></div>
                </div>

                {/* Book Pages Container (Revealed as cover opens) */}
                <div className="book-pages-theater">
                  {isLoadingPdf ? (
                    <div className="book-loading-spinner">
                      <div className="spinner-ring"></div>
                      <span>Opening CV Book...</span>
                    </div>
                  ) : viewMode === 'spread' && numPages >= 2 ? (
                    <div className="book-spread-inner">
                      {/* Left Page */}
                      <div className="book-page-wrapper left-page">
                        <div className="book-page-sheet">
                          <PDFPageCanvas
                            pdfDoc={pdfDoc}
                            pageNumber={currentPage}
                            width={pageWidth}
                          />
                          <div className="page-footer-num">
                            <span>PAGE {currentPage}</span>
                          </div>
                        </div>
                        <div className="page-spine-shadow-left"></div>
                      </div>

                      {/* Center Book Spine */}
                      <div className="book-center-spine">
                        <div className="spine-stitch"></div>
                        <div className="spine-crease"></div>
                        <div className="spine-ribbon"></div>
                      </div>

                      {/* Right Page */}
                      <div className="book-page-wrapper right-page">
                        <div className="book-page-sheet">
                          {currentPage + 1 <= numPages ? (
                            <PDFPageCanvas
                              pdfDoc={pdfDoc}
                              pageNumber={currentPage + 1}
                              width={pageWidth}
                            />
                          ) : (
                            <div className="book-end-sheet">
                              <div className="end-sheet-pattern">
                                <FiBookOpen className="end-icon" />
                                <h4>End of Document</h4>
                                <p>Marisilin Bijosilin • Curriculum Vitae</p>
                              </div>
                            </div>
                          )}
                          <div className="page-footer-num">
                            <span>PAGE {currentPage + 1}</span>
                          </div>
                        </div>
                        <div className="page-spine-shadow-right"></div>
                      </div>
                    </div>
                  ) : (
                    /* Single Page View */
                    <div className="book-single-inner">
                      <div className="book-page-wrapper single-page">
                        <div className="book-page-sheet">
                          <PDFPageCanvas
                            pdfDoc={pdfDoc}
                            pageNumber={currentPage}
                            width={pageWidth}
                          />
                          <div className="page-footer-num">
                            <span>PAGE {currentPage} OF {numPages || 1}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CVErrorBoundary>
        </main>

        {/* Bottom Floating Page Navigation (Active in Book Mode) */}
        {viewMode !== 'reader' && (
          <footer className="book-bottom-nav">
            <button
              className="book-nav-btn prev"
              onClick={handlePrevPage}
              disabled={currentPage <= 1}
              title="Previous Page (Left Arrow)"
              aria-label="Previous Page"
            >
              <FiChevronLeft /> <span>Previous</span>
            </button>

            <div className="book-page-indicator">
              <span className="page-pill">
                {viewMode === 'spread' && numPages >= 2
                  ? `Pages ${currentPage}–${Math.min(currentPage + 1, numPages)} of ${numPages}`
                  : `Page ${currentPage} of ${numPages || 1}`}
              </span>
            </div>

            <button
              className="book-nav-btn next"
              onClick={handleNextPage}
              disabled={
                viewMode === 'spread'
                  ? currentPage + 1 >= (numPages || 1)
                  : currentPage >= (numPages || 1)
              }
              title="Next Page (Right Arrow)"
              aria-label="Next Page"
            >
              <span>Next</span> <FiChevronRight />
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}
